import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, RefreshCw, Package } from 'lucide-react';
import useProductMargins from '../hooks/useProductMargins';
import './ProductMarginsTab.css';

const PERIODS = [
  { id: '7d',   label: '7d' },
  { id: '30d',  label: '30d' },
  { id: '90d',  label: '90d' },
  { id: 'year', label: 'Year' },
];

const SORTS = [
  { id: 'margin',  label: 'Margin %' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'units',   label: 'Units sold' },
];

const NAIRA = new Intl.NumberFormat('en-NG', {
  style: 'currency',
  currency: 'NGN',
  maximumFractionDigits: 0,
});
const fmtMoney = (n) => NAIRA.format(Number(n || 0));
const fmtPct   = (n) => `${Number(n || 0).toFixed(1)}%`;

/* ─── Module-scope sub-components ──────────────────────────────── */

function Toolbar({ period, sort, onPeriodChange, onSortChange }) {
  return (
    <div className="fin-margins-toolbar">
      <div className="fin-period-switcher" role="radiogroup" aria-label="Time period">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={period === p.id}
            className={`fin-period-pill${period === p.id ? ' is-active' : ''}`}
            onClick={() => onPeriodChange(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <label className="fin-margins-sort">
        <span className="fin-margins-sort-label">Sort by</span>
        <select
          className="fin-margins-sort-select"
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
        >
          {SORTS.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

function MarginPill({ pct }) {
  const tone = pct >= 30 ? 'high' : pct >= 10 ? 'mid' : pct >= 0 ? 'low' : 'negative';
  return <span className={`fin-margin-pill fin-margin-pill--${tone}`}>{fmtPct(pct)}</span>;
}

function ProductThumb({ src, alt }) {
  return src
    ? <img className="fin-margin-thumb" src={src} alt={alt} />
    : <div className="fin-margin-thumb fin-margin-thumb--placeholder" aria-hidden>
        <Package size={16} />
      </div>;
}

/* Mobile card row */
function MarginCard({ item }) {
  return (
    <Link to={`/products/${item.productId}/edit`} className="fin-margin-card">
      <ProductThumb src={item.image} alt={item.name} />
      <div className="fin-margin-card-body">
        <p className="fin-margin-card-name" title={item.name}>{item.name}</p>
        <div className="fin-margin-card-stats">
          <div>
            <p className="fin-margin-card-stat-label">Revenue</p>
            <p className="fin-margin-card-stat-value">{fmtMoney(item.revenue)}</p>
          </div>
          <div>
            <p className="fin-margin-card-stat-label">Profit</p>
            <p className="fin-margin-card-stat-value">{fmtMoney(item.grossProfit)}</p>
          </div>
          <div>
            <p className="fin-margin-card-stat-label">Units</p>
            <p className="fin-margin-card-stat-value">{item.unitsSold}</p>
          </div>
        </div>
      </div>
      <MarginPill pct={item.marginPct} />
    </Link>
  );
}

/* Desktop table row */
function MarginRow({ item }) {
  return (
    <tr>
      <td className="fin-margin-cell-name">
        <Link to={`/products/${item.productId}/edit`} className="fin-margin-row-link">
          <ProductThumb src={item.image} alt={item.name} />
          <span title={item.name}>{item.name}</span>
        </Link>
      </td>
      <td className="fin-margin-num">{item.unitsSold}</td>
      <td className="fin-margin-num">{fmtMoney(item.revenue)}</td>
      <td className="fin-margin-num">{fmtMoney(item.cogs)}</td>
      <td className="fin-margin-num">{fmtMoney(item.grossProfit)}</td>
      <td className="fin-margin-num"><MarginPill pct={item.marginPct} /></td>
    </tr>
  );
}

function EmptyState() {
  return (
    <div className="fin-empty">
      <Package size={40} className="fin-empty-icon" />
      <h2 className="fin-empty-title">No product sales yet for this period</h2>
      <p className="fin-empty-sub">
        Place an order containing a product (storefront, POS, WhatsApp,
        or manual sale) and its margin will land here automatically.
      </p>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="fin-error-banner" role="alert">
      <AlertCircle size={18} />
      <span>{message}</span>
      <button type="button" className="fin-error-retry" onClick={onRetry}>
        <RefreshCw size={14} /> Retry
      </button>
    </div>
  );
}

function SkeletonRows({ count = 6 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="fin-margin-card fin-margin-card--skeleton">
          <div className="fin-margin-thumb fin-margin-thumb--skeleton" />
          <div className="fin-margin-card-body">
            <div className="fin-skel-line fin-skel-line--name" />
            <div className="fin-skel-line fin-skel-line--sub" />
          </div>
          <div className="fin-skel-pill" />
        </div>
      ))}
    </>
  );
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function ProductMarginsTab() {
  const [period, setPeriod] = useState('7d');
  const [sort, setSort]     = useState('margin');
  const { items, loading, error, refetch } = useProductMargins({ period, sort, limit: 100 });

  return (
    <div className="fin-margins">
      <Toolbar
        period={period}
        sort={sort}
        onPeriodChange={setPeriod}
        onSortChange={setSort}
      />

      {error && <ErrorBanner message={error} onRetry={refetch} />}

      {!loading && !error && items.length === 0 && <EmptyState />}

      {!error && items.length > 0 && (
        <>
          {/* Mobile card list (default) */}
          <div className="fin-margins-cards">
            {items.map((item) => <MarginCard key={item.productId} item={item} />)}
          </div>

          {/* Desktop table (≥1024px) */}
          <div className="fin-margins-table-wrap">
            <table className="fin-margins-table">
              <thead>
                <tr>
                  <th className="fin-margin-cell-name">Product</th>
                  <th className="fin-margin-num">Units sold</th>
                  <th className="fin-margin-num">Revenue</th>
                  <th className="fin-margin-num">COGS</th>
                  <th className="fin-margin-num">Gross profit</th>
                  <th className="fin-margin-num">Margin</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => <MarginRow key={item.productId} item={item} />)}
              </tbody>
            </table>
          </div>
        </>
      )}

      {loading && items.length === 0 && (
        <div className="fin-margins-cards">
          <SkeletonRows count={6} />
        </div>
      )}
    </div>
  );
}
